import type { Notification } from './types';
import { dayHeading } from '@/features/Clients/activity';

export interface NotificationDayGroup {
  heading: string;
  items: Array<Notification>;
}

/**
 * Group the feed into day sections, preserving the server's order.
 *
 * The day boundary is `dayHeading` from the Clients timeline, imported rather
 * than re-derived: its own docstring says the local-midnight comparison is the
 * only part of this anyone gets wrong, and it is already unit-tested. Two
 * surfaces disagreeing about what "Yesterday" means would be a worse bug than
 * the import is a smell — and cross-feature imports are established here
 * (Onboarding → Clients, Settings → Team, and ten more pairs).
 *
 * This does NOT sort. The server already merges its eight sources newest-first
 * (`notifications/service.py`), so a change to that order surfaces here as
 * out-of-order headings instead of being silently corrected — which is what we
 * want, because the panel beside it shows the same order.
 *
 * An unparseable `at` yields dayHeading's own "Unknown date" and is grouped
 * under it rather than dropped: a notification that exists should be readable
 * even when its timestamp is not.
 */
export function groupNotificationsByDay(
  notifications: Array<Notification>,
  now: Date = new Date(),
): Array<NotificationDayGroup> {
  const out: Array<NotificationDayGroup> = [];
  for (const item of notifications) {
    const heading = dayHeading(item.at, now);
    const last = out.length > 0 ? out[out.length - 1] : undefined;
    if (last?.heading === heading) last.items.push(item);
    else out.push({ heading, items: [item] });
  }
  return out;
}
